/**
 * BreakdownRow — displays a single pot contribution within an aggregated payment.
 * Shows a pot-type avatar (gang vs solo), pot reference, status, and amount.
 */

import { Avatar, Box, Typography, alpha, useTheme } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import { formatCurrency } from '../../../shared/utils/currency';
import type { PaymentRowDto } from '../api/paymentsApi';

export const GANG_PREFIX = 'Gang - ';

interface BreakdownRowProps {
  row: PaymentRowDto;
  'data-testid'?: string;
}

export function BreakdownRow({
  row,
  'data-testid': dataTestId,
}: BreakdownRowProps) {
  const theme = useTheme();
  const isGang = row.potReference.startsWith(GANG_PREFIX);
  const isPaid = row.potStatus === 'Paid';

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Avatar
        sx={{
          width: theme.spacing(4),
          height: theme.spacing(4),
          bgcolor: alpha(
            theme.palette.text.primary,
            theme.palette.mode === 'dark' ? 0.08 : 0.06
          ),
          color: 'text.secondary',
          flexShrink: 0,
        }}
      >
        {isGang ? (
          <GroupsIcon sx={{ fontSize: '1.125rem' }} />
        ) : (
          <PersonIcon sx={{ fontSize: '1.125rem' }} />
        )}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.potReference}
        </Typography>
        <Typography
          variant="caption"
          color={isPaid ? 'success.main' : 'text.secondary'}
        >
          {row.potStatus}
        </Typography>
      </Box>

      <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
        {formatCurrency(row.amountDue)}
      </Typography>
    </Box>
  );
}
